import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, MessageSquare, Package, PhoneCall, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { riderApi, type Delivery } from "@/features/delivery/api";
import { useLiveGpsPusher } from "@/features/delivery/useLiveGpsPusher";
import { formatApiError } from "@/features/auth/formatError";
import { Input } from "@/components/ui/input";
import { ChatPanel } from "@/components/delivery/ChatPanel";

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

const STAGES: Array<{ key: Delivery["status"]; label: string }> = [
    { key: "accepted", label: "Head to store" },
    { key: "picked_up", label: "Picked up" },
    { key: "out_for_delivery", label: "En route" },
    { key: "delivered", label: "Delivered" },
];

export default function RiderActive() {
    const qc = useQueryClient();
    const [otp, setOtp] = useState("");
    const [chatFor, setChatFor] = useState<string | null>(null);

    const { data: active = [], isLoading } = useQuery<Delivery[]>({
        queryKey: ["rider", "active"],
        queryFn: riderApi.active,
        refetchInterval: 8000,
    });

    // Push rider GPS to the backend every 5 seconds while there's an
    // active delivery — powers the customer's live map.
    useLiveGpsPusher(active.length > 0, 5000);

    const pickupMut = useMutation({
        mutationFn: (id: string) => riderApi.pickup(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rider", "active"] });
            toast.success("Pickup confirmed");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't confirm pickup")),
    });
    const outMut = useMutation({
        mutationFn: (id: string) => riderApi.outForDelivery(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rider", "active"] });
            toast.success("Out for delivery — drive safe!");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't update status")),
    });
    const otpMut = useMutation({
        mutationFn: ({ id, code }: { id: string; code: string }) => riderApi.verifyOtp(id, code),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rider", "active"] });
            qc.invalidateQueries({ queryKey: ["rider", "earnings"] });
            setOtp("");
            toast.success("Delivered! Nice work.");
        },
        onError: (err) => toast.error(formatApiError(err, "OTP verification failed")),
    });

    if (isLoading) {
        return <p className="text-sm text-muted-foreground">Loading active deliveries…</p>;
    }
    if (active.length === 0) {
        return (
            <div className="rounded-2xl border border-dashed border-border/70 bg-card/50 p-8 text-center" data-testid="rider-active-empty">
                <Truck className="mx-auto h-8 w-8 text-brand" />
                <p className="mt-2 text-sm font-semibold">No active deliveries</p>
                <p className="text-xs text-muted-foreground">
                    Accept an offer from the Home tab to start one.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="rider-active-page">
            {active.map((d) => {
                const currentIdx = STAGES.findIndex((s) => s.key === d.status);
                return (
                    <article key={d.id} className="rounded-2xl border border-border/70 bg-card p-4 shadow-soft" data-testid={`rider-active-${d.id}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-bold">{d.order?.number}</p>
                                <p className="text-xs text-muted-foreground">
                                    {d.order?.item_count} items · {rupee(d.order?.total || 0)} · Earn {rupee(d.earnings)}
                                </p>
                            </div>
                            <span className="rounded-full bg-brand px-2 py-1 text-[10px] font-bold uppercase text-white">
                                {d.status.replace(/_/g, " ")}
                            </span>
                        </div>

                        {/* Stage progress */}
                        <ol className="mt-4 flex items-center justify-between text-[10px] font-medium">
                            {STAGES.map((s, i) => (
                                <li key={s.key} className="flex flex-col items-center gap-1">
                                    <span
                                        className={`grid h-6 w-6 place-items-center rounded-full text-white ${
                                            i <= currentIdx ? "bg-brand" : "bg-muted text-muted-foreground"
                                        }`}
                                    >
                                        {i <= currentIdx ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                                    </span>
                                    <span className={i <= currentIdx ? "text-foreground" : "text-muted-foreground"}>{s.label}</span>
                                </li>
                            ))}
                        </ol>

                        {/* Address */}
                        <div className="mt-4 rounded-xl bg-muted/40 p-3 text-sm">
                            <p className="font-semibold">{d.order?.address.full_name}</p>
                            <p className="text-xs text-muted-foreground">
                                {d.order?.address.line1}, {d.order?.address.line2}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {d.order?.address.city}, {d.order?.address.pincode}
                            </p>

                            {/* Route summary — Rider→Store, Store→Customer, ETA */}
                            {(() => {
                                const store = (d.order as unknown as { store?: { geo?: { lat: number; lng: number }; name?: string; address_line1?: string } })?.store;
                                const customer = (d.order?.address as unknown as { gps?: { lat: number; lng: number } })?.gps;
                                const rider = (d as unknown as { rider_location?: { lat: number; lng: number } })?.rider_location;
                                const km = (a?: { lat: number; lng: number }, b?: { lat: number; lng: number }) => {
                                    if (!a?.lat || !b?.lat) return null;
                                    const R = 6371;
                                    const p1 = (a.lat * Math.PI) / 180;
                                    const p2 = (b.lat * Math.PI) / 180;
                                    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
                                    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
                                    const h = Math.sin(dLat / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dLng / 2) ** 2;
                                    return 2 * R * Math.asin(Math.sqrt(h));
                                };
                                const rs = km(rider, store?.geo);
                                const sc = km(store?.geo, customer);
                                const total = (rs ?? 0) + (sc ?? 0);
                                const etaMin = total ? Math.max(5, Math.round((total / 25) * 60)) : null;
                                if (rs == null && sc == null) return null;
                                return (
                                    <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-white p-2 text-center text-[11px]" data-testid={`rider-route-summary-${d.id}`}>
                                        <div>
                                            <p className="text-muted-foreground">Rider → Store</p>
                                            <p className="font-bold">{rs != null ? `${rs.toFixed(1)} km` : "—"}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">Store → Customer</p>
                                            <p className="font-bold">{sc != null ? `${sc.toFixed(1)} km` : "—"}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground">ETA</p>
                                            <p className="font-bold">{etaMin ? `${etaMin} min` : "—"}</p>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Payment badge — riders MUST see whether cash is due. */}
                            {d.order?.payment?.method === "cod" ? (
                                <p
                                    data-testid={`rider-payment-badge-${d.id}`}
                                    className="mt-2 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800"
                                >
                                    Collect Cash · ₹{d.order?.total?.toLocaleString?.("en-IN") ?? ""}
                                </p>
                            ) : (
                                <p
                                    data-testid={`rider-payment-badge-${d.id}`}
                                    className="mt-2 inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700"
                                >
                                    Already Paid
                                </p>
                            )}
                            {/* Dual navigation — before pickup guide to Store, after pickup to Customer */}
                            {(() => {
                                const store = (d.order as unknown as { store?: { geo?: { lat: number; lng: number } } })?.store;
                                const cust = (d.order?.address as unknown as { gps?: { lat: number; lng: number } })?.gps;
                                const beforePickup = ["assigned", "accepted"].includes(d.status);
                                const storeUrl = store?.geo?.lat
                                    ? `https://www.google.com/maps/dir/?api=1&destination=${store.geo.lat},${store.geo.lng}&travelmode=driving`
                                    : null;
                                const custUrl = cust?.lat
                                    ? `https://www.google.com/maps/dir/?api=1&destination=${cust.lat},${cust.lng}&travelmode=driving`
                                    : d.order?.maps_url;
                                return (
                                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                        {storeUrl && (
                                            <a
                                                href={storeUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                data-testid={`rider-nav-store-${d.id}`}
                                                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-bold ${
                                                    beforePickup
                                                        ? "bg-ink text-white"
                                                        : "border border-border bg-white text-ink"
                                                }`}
                                            >
                                                <ExternalLink className="h-3.5 w-3.5" /> Navigate to Store
                                            </a>
                                        )}
                                        {custUrl && (
                                            <a
                                                href={custUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                data-testid={`rider-nav-customer-${d.id}`}
                                                className={`inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-bold ${
                                                    beforePickup
                                                        ? "border border-border bg-white text-ink"
                                                        : "bg-brand text-white"
                                                }`}
                                            >
                                                <ExternalLink className="h-3.5 w-3.5" /> Navigate to Customer
                                            </a>
                                        )}
                                    </div>
                                );
                            })()}
                            <a
                                href={`tel:${d.order?.address.phone}`}
                                className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-bold hover:bg-muted"
                            >
                                <PhoneCall className="h-3.5 w-3.5" /> Call customer
                            </a>
                        </div>

                        {/* Action for current stage */}
                        {d.status === "accepted" ? (
                            <button
                                type="button"
                                onClick={() => pickupMut.mutate(d.id)}
                                disabled={pickupMut.isPending}
                                data-testid={`rider-pickup-${d.id}`}
                                className="mt-4 w-full rounded-full bg-brand py-3 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                            >
                                <Package className="mr-1 inline-block h-4 w-4" /> Confirm pickup
                            </button>
                        ) : d.status === "picked_up" ? (
                            <button
                                type="button"
                                onClick={() => outMut.mutate(d.id)}
                                disabled={outMut.isPending}
                                data-testid={`rider-out-${d.id}`}
                                className="mt-4 w-full rounded-full bg-brand py-3 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                            >
                                <Truck className="mr-1 inline-block h-4 w-4" /> Mark out for delivery
                            </button>
                        ) : d.status === "out_for_delivery" ? (
                            <div className="mt-4 rounded-xl border border-brand/30 bg-brand/5 p-3">
                                <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand">
                                    <ShieldCheck className="h-3.5 w-3.5" /> Ask customer for 4-digit OTP
                                </label>
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                    OTP for this order: <span className="font-mono font-bold">{d.otp}</span>{" "}
                                    <span className="text-[10px]">(also shown to the customer in the app)</span>
                                </p>
                                <div className="mt-2 flex gap-2">
                                    <Input
                                        inputMode="numeric"
                                        maxLength={4}
                                        placeholder="1234"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                        className="text-center text-lg font-mono tracking-widest"
                                        data-testid={`rider-otp-input-${d.id}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => otpMut.mutate({ id: d.id, code: otp })}
                                        disabled={otpMut.isPending || otp.length < 4}
                                        data-testid={`rider-verify-otp-${d.id}`}
                                        className="rounded-full bg-brand px-4 text-sm font-bold text-white shadow-brand hover:bg-brand-600 disabled:opacity-50"
                                    >
                                        Verify
                                    </button>
                                </div>
                            </div>
                        ) : null}
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={() => setChatFor(chatFor === d.order?.id ? null : d.order?.id || null)}
                                data-testid={`rider-chat-toggle-${d.id}`}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline"
                            >
                                <MessageSquare className="h-3.5 w-3.5" />
                                {chatFor === d.order?.id ? "Hide chat" : "Chat with customer"}
                            </button>
                        </div>
                        {chatFor === d.order?.id && d.order?.id ? (
                            <div className="mt-3">
                                <ChatPanel orderId={d.order.id} me="rider" />
                            </div>
                        ) : null}
                    </article>
                );
            })}
        </div>
    );
}
